/**
 * Analysis Results Modal — Enterprise Edition
 *
 * Displays comprehensive deforestation analysis results including:
 * - Compliance status with confidence rating
 * - Dual-index (NDVI + NBR) statistics
 * - Sensitivity analysis at 3 threshold levels
 * - Per-patch deforestation details with confidence scores
 * - Data quality / coverage metrics
 * - Satellite imagery including observation density
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    X, CheckCircle, AlertTriangle, TrendingDown, Leaf,
    BarChart3, Shield, Layers, Activity, Eye, Image, FileText, Loader2,
} from 'lucide-react';
import { analysisApi } from '../../lib/api';
import ConfidenceBadge from './ConfidenceBadge';
import QualityMetadataPanel from './QualityMetadataPanel';

interface AnalysisResultsModalProps {
    job: any;
    onClose: () => void;
}

export default function AnalysisResultsModal({ job, onClose }: AnalysisResultsModalProps) {
    const { t } = useTranslation();
    const [imageBlobUrls, setImageBlobUrls] = useState<Record<string, string>>({});
    const [imageryLoading, setImageryLoading] = useState(false);
    const [activeTab, setActiveTab] = useState<'overview' | 'indices' | 'sensitivity' | 'quality' | 'imagery' | 'gallery' | 'scenes'>('overview');

    const results = job.results_json || {};
    const quality = results.quality_metrics || {};
    const sensitivity = results.sensitivity_analysis || {};
    const patches = results.deforestation_patches || [];

    // Fetch imagery via authenticated API as blob URLs
    useEffect(() => {
        const imageryKeys = results.imagery_paths
            ? Object.keys(results.imagery_paths)
            : [];
        if (imageryKeys.length === 0) return;

        let cancelled = false;
        setImageryLoading(true);
        (async () => {
            const urls: Record<string, string> = {};
            await Promise.all(
                imageryKeys.map(async (key: string) => {
                    const blobUrl = await analysisApi.getImageryBlobUrl(job.id, key);
                    if (blobUrl) urls[key] = blobUrl;
                })
            );
            if (!cancelled) {
                setImageBlobUrls(urls);
                setImageryLoading(false);
            }
        })();

        return () => {
            cancelled = true;
            Object.values(imageBlobUrls).forEach(url => URL.revokeObjectURL(url));
        };
    }, [job.id]);

    const tabs = [
        { id: 'overview' as const, label: t('analysisResults.tabs.overview'), icon: Leaf },
        { id: 'indices' as const, label: t('analysisResults.tabs.indices'), icon: Activity },
        { id: 'sensitivity' as const, label: t('analysisResults.tabs.sensitivity'), icon: BarChart3 },
        { id: 'quality' as const, label: t('analysisResults.tabs.quality'), icon: Shield },
        { id: 'imagery' as const, label: t('analysisResults.tabs.imagery'), icon: Eye },
        { id: 'gallery' as const, label: 'Gallery', icon: Image },
        { id: 'scenes' as const, label: t('analysisResults.tabs.scenes', 'Scene Report'), icon: FileText },
    ];

    return (
        <div className="fixed inset-0 z-[1001] flex items-center justify-center p-4 bg-black/50 animate-fade-in">
            <div className="card p-0 w-full max-w-5xl animate-slide-up max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex items-center gap-3 min-w-0">
                        <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
                            <Leaf className="w-6 h-6 text-primary-600" />
                        </div>
                        <div className="min-w-0">
                            <h2 className="text-xl font-bold text-gray-900 dark:text-white truncate">
                                {t('analysisResults.title')}
                            </h2>
                            <p className="text-sm text-gray-500">
                                {job.polygon_name} &middot;{' '}
                                {results.actual_monitor_range
                                    ? results.actual_monitor_range
                                    : `${job.baseline_year} → ${job.monitoring_year}`}
                                {results.detection_method && (
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                        Enterprise
                                    </span>
                                )}
                                {results.sar_enabled && (
                                    <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                        {t('analysisResults.sarHybrid')}
                                    </span>
                                )}
                                {results.monitoring_window_type && results.monitoring_window_type.startsWith('rolling') && (
                                    <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                                        {t('analysisResults.adaptiveWindow')}
                                    </span>
                                )}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* Tab Navigation */}
                <div className="flex border-b border-gray-200 dark:border-gray-700 px-3 sm:px-6 overflow-x-auto">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-1.5 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                                activeTab === tab.id
                                    ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                            }`}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="p-4 sm:p-6 space-y-6">

                    {/* ═══ TAB: OVERVIEW ═══ */}
                    {activeTab === 'overview' && (
                        <>
                            {/* Compliance Status */}
                            <div className={`rounded-xl p-6 border-2 ${
                                job.compliance_status === 'compliant'
                                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                            }`}>
                                <div className="flex items-center gap-3 mb-3">
                                    {job.compliance_status === 'compliant' ? (
                                        <CheckCircle className="w-8 h-8 text-green-600" />
                                    ) : (
                                        <AlertTriangle className="w-8 h-8 text-red-600" />
                                    )}
                                    <div className="flex-1">
                                        <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                            {job.compliance_status === 'compliant' ? t('analysisResults.compliantEudr') : t('analysisResults.nonCompliantEudr')}
                                        </h3>
                                        <p className="text-sm text-gray-600 dark:text-gray-400">
                                            {t('analysisResults.riskLevel')}: <span className="font-medium">{job.risk_level || 'N/A'}</span>
                                            {quality.analysis_confidence && (
                                                <span className="ml-3">
                                                    {t('analysisResults.analysisConfidence')}:{' '}
                                                    <span className={`font-semibold ${
                                                        quality.analysis_confidence === 'high' ? 'text-green-600' :
                                                        quality.analysis_confidence === 'medium' ? 'text-yellow-600' :
                                                        'text-red-600'
                                                    }`}>
                                                        {quality.analysis_confidence === 'high' ? t('analysisResults.confidenceHigh') :
                                                         quality.analysis_confidence === 'medium' ? t('analysisResults.confidenceMedium') : t('analysisResults.confidenceLow')}
                                                    </span>
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                </div>
                                {results.compliance_note && (
                                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2 pl-11">
                                        {results.compliance_note}
                                    </p>
                                )}
                                {/* Confidence badge row */}
                                <div className="flex items-center gap-2 mt-3 pl-11">
                                    <ConfidenceBadge
                                        level={job.confidence_level || quality.analysis_confidence}
                                        isEstimated={job.is_estimated}
                                        size="md"
                                    />
                                </div>
                            </div>

                            {/* Quality & Traceability metadata */}
                            <QualityMetadataPanel
                                algorithmVersion={job.algorithm_version}
                                isEstimated={job.is_estimated}
                                confidenceLevel={job.confidence_level}
                                analysisDate={job.completed_at}
                                baselineYear={job.baseline_year}
                                monitoringYear={job.monitoring_year}
                            />

                            {/* Data quality notes */}
                            {job.data_quality_notes && (
                                <div className="rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 p-3">
                                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Limitations & Notes
                                    </p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {job.data_quality_notes}
                                    </p>
                                </div>
                            )}

                            {/* Main Metrics */}
                            <div>
                                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                    {t('analysisResults.changeAnalysisResults')}
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                        <p className="text-xs text-gray-500 mb-1">{t('analysisResults.totalAnalyzedArea')}</p>
                                        <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                            {job.area_total_ha?.toFixed(2) || 'N/A'}
                                        </p>
                                        <p className="text-xs text-gray-500">{t('common.hectares')}</p>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-line dark:border-gray-700">
                                        <p className="text-xs text-gray-500 mb-1">{t('analysisResults.deforestedArea')}</p>
                                        <p className="text-2xl font-bold text-red-600">
                                            {job.area_deforested_ha?.toFixed(4) || 'N/A'}
                                        </p>
                                        <p className="text-xs text-gray-500">{t('common.hectares')}</p>
                                    </div>
                                    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-line dark:border-gray-700">
                                        <p className="text-xs text-gray-500 mb-1">{t('analysisResults.lossPercentage')}</p>
                                        <p className="text-2xl font-bold text-red-600">
                                            {job.deforestation_percentage?.toFixed(3) || 'N/A'}%
                                        </p>
                                        <p className="text-xs text-gray-500">{t('analysisResults.ofAnalyzedArea')}</p>
                                    </div>
                                </div>
                            </div>

                            {/* Detection Method Badge */}
                            {results.detection_method && (
                                <div className="bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg p-4">
                                    <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                                        {t('analysisResults.detectionMethodology')}
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                                        <div>
                                            <p className="text-gray-500 dark:text-gray-400 font-medium">{t('analysisResults.method')}</p>
                                            <p className="text-gray-700 dark:text-gray-300">
                                                {results.sar_enabled
                                                    ? t('analysisResults.hybridOpticalSar')
                                                    : t('analysisResults.ensembleDualIndex')}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 dark:text-gray-400 font-medium">{t('analysisResults.indicesLabel')}</p>
                                            <p className="text-gray-700 dark:text-gray-300">
                                                {(results.indices_used || []).join(' + ')}
                                                {results.sar_enabled && ' + SAR VH/VV'}
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 dark:text-gray-400 font-medium">{t('analysisResults.significance')}</p>
                                            <p className="text-gray-700 dark:text-gray-300">
                                                z &gt; {results.z_score_level ?? 1.96} ({results.significance_level ?? '95% CI'})
                                            </p>
                                        </div>
                                        <div>
                                            <p className="text-gray-500 dark:text-gray-400 font-medium">{t('analysisResults.falsePositive')}</p>
                                            <p className="text-gray-700 dark:text-gray-300">{results.joint_false_positive ?? '~0.25%/px'}</p>
                                        </div>
                                    </div>
                                    {results.sar_enabled && results.sar_merge_zones && (
                                        <div className="mt-3 pt-3 border-t border-line dark:border-gray-700">
                                            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('analysisResults.sarHybridCoverage')}</p>
                                            <div className="flex flex-wrap gap-3 text-xs text-gray-700 dark:text-gray-300">
                                                <span>{t('analysisResults.opticalOnly')}: {results.sar_merge_zones.optical_only_px?.toLocaleString()} px</span>
                                                <span>{t('analysisResults.sarOnly')}: {results.sar_merge_zones.sar_only_px?.toLocaleString()} px</span>
                                                <span>{t('analysisResults.both')}: {results.sar_merge_zones.both_px?.toLocaleString()} px</span>
                                                <span className="font-medium text-gray-700 dark:text-gray-300">
                                                    SAR gap-fill: {results.sar_merge_zones.sar_gap_fill_pct}%
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Deforestation Patches */}
                            {patches.length > 0 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                                        <Layers className="w-4 h-4" />
                                        {t('analysisResults.deforestationPatches', { count: patches.length })}
                                    </h3>
                                    <div className="overflow-x-auto">
                                        <table className="w-full text-xs">
                                            <thead>
                                                <tr className="border-b border-gray-200 dark:border-gray-700">
                                                    <th className="text-left py-2 px-3 text-gray-500 font-medium">{t('analysisResults.patch')}</th>
                                                    <th className="text-right py-2 px-3 text-gray-500 font-medium">{t('analysisResults.pixels')}</th>
                                                    <th className="text-right py-2 px-3 text-gray-500 font-medium">{t('analysisResults.areaHa')}</th>
                                                    <th className="text-right py-2 px-3 text-gray-500 font-medium">{t('analysisResults.confidence')}</th>
                                                    <th className="text-right py-2 px-3 text-gray-500 font-medium">|z| NDVI</th>
                                                    <th className="text-right py-2 px-3 text-gray-500 font-medium">|z| NBR</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {patches.map((patch: any, idx: number) => (
                                                    <tr key={idx} className="border-b border-gray-100 dark:border-gray-800">
                                                        <td className="py-2 px-3 font-medium text-gray-700 dark:text-gray-300">
                                                            #{idx + 1}
                                                        </td>
                                                        <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-400">
                                                            {patch.pixels}
                                                        </td>
                                                        <td className="py-2 px-3 text-right font-medium text-red-600">
                                                            {patch.area_ha}
                                                        </td>
                                                        <td className="py-2 px-3 text-right">
                                                            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                                                patch.confidence >= 0.7
                                                                    ? 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                                                                    : patch.confidence >= 0.4
                                                                    ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300'
                                                                    : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                                                            }`}>
                                                                {(patch.confidence * 100).toFixed(0)}%
                                                            </span>
                                                        </td>
                                                        <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-400">
                                                            {patch.mean_abs_z_ndvi}
                                                        </td>
                                                        <td className="py-2 px-3 text-right text-gray-600 dark:text-gray-400">
                                                            {patch.mean_abs_z_nbr}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </>
                    )}

                    {/* ═══ TAB: INDICES ═══ */}
                    {activeTab === 'indices' && (
                        <>
                            {/* NDVI Section */}
                            <div>
                                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                                    <TrendingDown className="w-4 h-4" />
                                    {t('analysisResults.ndviTitle')}
                                </h3>
                                <p className="text-xs text-gray-500 mb-3">
                                    {t('analysisResults.ndviDescription')}
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <MetricCard
                                        label={`NDVI Baseline (${job.baseline_year})`}
                                        value={job.ndvi_baseline_mean?.toFixed(4)}
                                        subtitle={t('analysisResults.baselinePeriodMedian')}
                                    />
                                    <MetricCard
                                        label={`NDVI Monitor (${job.monitoring_year})`}
                                        value={job.ndvi_monitoring_mean?.toFixed(4)}
                                        subtitle={t('analysisResults.currentPeriodMedian')}
                                    />
                                    <MetricCard
                                        label={t('analysisResults.ndviChange')}
                                        value={`${(job.ndvi_change || 0) >= 0 ? '+' : ''}${job.ndvi_change?.toFixed(4) || 'N/A'}`}
                                        subtitle={`${t('analysisResults.threshold')}: ${results.threshold_used ?? -0.15}`}
                                        highlight={job.ndvi_change < 0 ? 'red' : 'green'}
                                    />
                                </div>
                            </div>

                            {/* NBR Section */}
                            {results.nbr_baseline_mean != null && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                                        <Activity className="w-4 h-4" />
                                        {t('analysisResults.nbrTitle')}
                                    </h3>
                                    <p className="text-xs text-gray-500 mb-3">
                                        {t('analysisResults.nbrDescription')}
                                    </p>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <MetricCard
                                            label={`NBR Baseline (${job.baseline_year})`}
                                            value={results.nbr_baseline_mean?.toFixed(4)}
                                            subtitle={t('analysisResults.baselinePeriodMedian')}
                                        />
                                        <MetricCard
                                            label={`NBR Monitor (${job.monitoring_year})`}
                                            value={results.nbr_monitoring_mean?.toFixed(4)}
                                            subtitle={t('analysisResults.currentPeriodMedian')}
                                        />
                                        <MetricCard
                                            label={t('analysisResults.nbrChange')}
                                            value={`${(results.nbr_change || 0) >= 0 ? '+' : ''}${results.nbr_change?.toFixed(4) || 'N/A'}`}
                                            subtitle={`${t('analysisResults.threshold')}: ${results.nbr_threshold_used ?? -0.10}`}
                                            highlight={results.nbr_change < 0 ? 'red' : 'green'}
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Dual-Index Explanation */}
                            <div className="bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg p-4">
                                <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
                                    {t('analysisResults.dualIndexTitle')}
                                </h4>
                                <p className="text-xs text-gray-600 dark:text-gray-300">
                                    {t('analysisResults.dualIndexDescription', { zScore: results.z_score_level ?? 1.96, significance: results.significance_level ?? '95% CI' })}
                                </p>
                            </div>
                        </>
                    )}

                    {/* ═══ TAB: SENSITIVITY ═══ */}
                    {activeTab === 'sensitivity' && (
                        <>
                            <div>
                                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                                    {t('analysisResults.sensitivityTitle')}
                                </h3>
                                <p className="text-xs text-gray-500 mb-4">
                                    {t('analysisResults.sensitivityDescription')}
                                </p>
                            </div>

                            {Object.keys(sensitivity).length > 0 ? (
                                <div className="space-y-3">
                                    {Object.entries(sensitivity).map(([level, data]: [string, any]) => {
                                        const isStandard = level === 'standard';
                                        return (
                                            <div
                                                key={level}
                                                className={`rounded-lg p-4 border ${
                                                    isStandard
                                                        ? 'border-primary-300 dark:border-primary-700 bg-primary-50 dark:bg-primary-900/20'
                                                        : 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800'
                                                }`}
                                            >
                                                <div className="flex items-center justify-between mb-2">
                                                    <div className="flex items-center gap-2">
                                                        <span className={`text-sm font-semibold capitalize ${
                                                            isStandard ? 'text-primary-700 dark:text-primary-300' : 'text-gray-700 dark:text-gray-300'
                                                        }`}>
                                                            {level === 'conservative' ? t('analysisResults.sensitivityConservative') :
                                                             level === 'standard' ? t('analysisResults.sensitivityStandard') :
                                                             t('analysisResults.sensitivitySensitive')}
                                                        </span>
                                                        <span className="text-xs text-gray-500 bg-gray-200 dark:bg-gray-700 px-2 py-0.5 rounded">
                                                            {data.label}
                                                        </span>
                                                    </div>
                                                    <span className="text-lg font-bold text-gray-900 dark:text-white">
                                                        {data.area_ha} ha
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-4 text-xs text-gray-500">
                                                    <span>NDVI &Delta; &lt; {data.threshold_ndvi}</span>
                                                    <span>z &gt; {data.z_score_level}</span>
                                                    <span>{data.pixels} px</span>
                                                    <span>{data.percentage}% {t('analysisResults.ofArea')}</span>
                                                    {data.quality_adaptive && (
                                                        <span className="text-gray-500 dark:text-gray-400 font-medium" title="Thresholds are adjusted per-pixel based on observation quality. Low-quality pixels (more cloud-affected) require stronger evidence.">
                                                            QA
                                                        </span>
                                                    )}
                                                </div>
                                                {/* Visual bar */}
                                                <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full rounded-full ${
                                                            isStandard ? 'bg-primary-500' : 'bg-gray-400 dark:bg-gray-500'
                                                        }`}
                                                        style={{ width: `${Math.min(100, Math.max(1, data.percentage * 10))}%` }}
                                                    />
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">{t('analysisResults.sensitivityNotAvailable')}</p>
                            )}

                            <div className="bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg p-3">
                                <p className="text-xs text-gray-700 dark:text-gray-300">
                                    <strong>{t('analysisResults.interpretation')}:</strong> {t('analysisResults.sensitivityInterpretation')}
                                </p>
                            </div>
                        </>
                    )}

                    {/* ═══ TAB: QUALITY ═══ */}
                    {activeTab === 'quality' && (
                        <>
                            {/* Confidence Score */}
                            {quality.analysis_confidence_score != null && (
                                <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
                                    <div className="relative w-20 h-20 flex-shrink-0">
                                        <svg className="w-20 h-20 transform -rotate-90" viewBox="0 0 36 36">
                                            <path
                                                d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                                fill="none"
                                                stroke="#e5e7eb"
                                                strokeWidth="3"
                                            />
                                            <path
                                                d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                                                fill="none"
                                                stroke={quality.analysis_confidence === 'high' ? '#16a34a' :
                                                        quality.analysis_confidence === 'medium' ? '#ca8a04' : '#dc2626'}
                                                strokeWidth="3"
                                                strokeDasharray={`${quality.analysis_confidence_score * 100}, 100`}
                                            />
                                        </svg>
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-lg font-bold text-gray-900 dark:text-white">
                                                {(quality.analysis_confidence_score * 100).toFixed(0)}%
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex-1">
                                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                            {t('analysisResults.analysisConfidence')}:{' '}
                                            <span className={
                                                quality.analysis_confidence === 'high' ? 'text-green-600' :
                                                quality.analysis_confidence === 'medium' ? 'text-yellow-600' : 'text-red-600'
                                            }>
                                                {quality.analysis_confidence === 'high' ? t('analysisResults.confidenceHigh') :
                                                 quality.analysis_confidence === 'medium' ? t('analysisResults.confidenceMedium') : t('analysisResults.confidenceLow')}
                                            </span>
                                        </h3>
                                        <p className="text-xs text-gray-500 mt-1">
                                            {t('analysisResults.confidenceWeightedAverage')}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Confidence Factor Breakdown */}
                            {quality.confidence_factors && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                        {t('analysisResults.confidenceFactors')}
                                    </h3>
                                    <div className="space-y-2">
                                        {(results.sar_enabled ? [
                                            { key: 'obs_density_baseline', label: t('analysisResults.obsDensityBaseline'), weight: '12%' },
                                            { key: 'obs_density_monitor', label: t('analysisResults.obsDensityMonitor'), weight: '12%' },
                                            { key: 'spatial_overlap', label: t('analysisResults.spatialOverlap'), weight: '20%' },
                                            { key: 'min_period_coverage', label: t('analysisResults.minPeriodCoverage'), weight: '12%' },
                                            { key: 'scene_count', label: t('analysisResults.sceneCount'), weight: '12%' },
                                            { key: 'pixel_quality', label: t('analysisResults.pixelQuality', 'Pixel quality (cloud impact)'), weight: '17%' },
                                            { key: 'sar_gap_fill', label: t('analysisResults.sarGapFill'), weight: '15%' },
                                        ] : [
                                            { key: 'obs_density_baseline', label: t('analysisResults.obsDensityBaseline'), weight: '15%' },
                                            { key: 'obs_density_monitor', label: t('analysisResults.obsDensityMonitor'), weight: '15%' },
                                            { key: 'spatial_overlap', label: t('analysisResults.spatialOverlap'), weight: '25%' },
                                            { key: 'min_period_coverage', label: t('analysisResults.minPeriodCoverage'), weight: '12%' },
                                            { key: 'scene_count', label: t('analysisResults.sceneCount'), weight: '13%' },
                                            { key: 'pixel_quality', label: t('analysisResults.pixelQuality', 'Pixel quality (cloud impact)'), weight: '20%' },
                                        ]).map(f => {
                                            const val = quality.confidence_factors[f.key] ?? 0;
                                            return (
                                                <div key={f.key} className="flex items-center gap-3">
                                                    <span className="text-xs text-gray-500 w-40 flex-shrink-0">{f.label} ({f.weight})</span>
                                                    <div className="flex-1 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                                                        <div
                                                            className={`h-full rounded-full ${
                                                                val >= 0.7 ? 'bg-green-500' : val >= 0.4 ? 'bg-yellow-500' : 'bg-red-500'
                                                            }`}
                                                            style={{ width: `${Math.min(100, val * 100)}%` }}
                                                        />
                                                    </div>
                                                    <span className="text-xs font-mono text-gray-600 dark:text-gray-400 w-10 text-right">
                                                        {(val * 100).toFixed(0)}%
                                                    </span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {/* Coverage Metrics */}
                            <div>
                                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                    {t('analysisResults.satelliteDataCoverage')}
                                </h3>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <CoverageCard
                                        label={`Baseline (${job.baseline_year})`}
                                        pct={quality.coverage_baseline_pct}
                                        scenes={quality.scenes_baseline}
                                        meanObs={quality.mean_obs_baseline}
                                    />
                                    <CoverageCard
                                        label={`Monitor (${job.monitoring_year})`}
                                        pct={quality.coverage_monitor_pct}
                                        scenes={quality.scenes_monitor}
                                        meanObs={quality.mean_obs_monitor}
                                    />
                                    <CoverageCard
                                        label={t('analysisResults.overlapBothPeriods')}
                                        pct={quality.coverage_overlap_pct}
                                        highlight
                                    />
                                </div>
                            </div>

                            {/* SAR Coverage (when available) */}
                            {results.sar_enabled && results.scenes_acquired?.sar_total > 0 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                                        <Layers className="w-4 h-4" />
                                        {t('analysisResults.sarCoverage')}
                                    </h3>
                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                        <CoverageCard
                                            label={`SAR Baseline (${job.baseline_year})`}
                                            pct={quality.sar_coverage_baseline_pct}
                                            scenes={results.scenes_acquired?.sar_baseline}
                                            meanObs={quality.sar_mean_obs_baseline}
                                        />
                                        <CoverageCard
                                            label={`SAR Monitor (${job.monitoring_year})`}
                                            pct={quality.sar_coverage_monitor_pct}
                                            scenes={results.scenes_acquired?.sar_monitor}
                                            meanObs={quality.sar_mean_obs_monitor}
                                        />
                                        {results.sar_merge_zones && (
                                            <div className="rounded-lg p-4 bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700">
                                                <p className="text-xs text-gray-500 mb-1">SAR Gap-Fill</p>
                                                <p className="text-2xl font-bold text-gray-900 dark:text-white">
                                                    {results.sar_merge_zones.sar_gap_fill_pct}%
                                                </p>
                                                <p className="text-xs text-gray-500 mt-1">
                                                    {results.sar_merge_zones.sar_only_px?.toLocaleString()} {t('analysisResults.pxCoveredByRadarOnly')}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Per-Pixel Quality Distribution */}
                            {quality.pixel_quality_distribution && quality.pixel_quality_distribution.mean > 0 && (
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                                        <BarChart3 className="w-4 h-4" />
                                        {t('analysisResults.pixelQualityDist', 'Per-Pixel Quality Distribution')}
                                    </h3>
                                    <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                                            <div className="text-center">
                                                <p className="text-xs text-gray-500">Mean</p>
                                                <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                    {(quality.pixel_quality_distribution.mean * 100).toFixed(0)}%
                                                </p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-xs text-gray-500">Median</p>
                                                <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                    {(quality.pixel_quality_distribution.median * 100).toFixed(0)}%
                                                </p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-xs text-gray-500">P10</p>
                                                <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                    {(quality.pixel_quality_distribution.p10 * 100).toFixed(0)}%
                                                </p>
                                            </div>
                                            <div className="text-center">
                                                <p className="text-xs text-gray-500">P75</p>
                                                <p className="text-lg font-bold text-gray-900 dark:text-white">
                                                    {(quality.pixel_quality_distribution.p75 * 100).toFixed(0)}%
                                                </p>
                                            </div>
                                        </div>
                                        {quality.pixel_quality_distribution.pct_below_050 > 0 && (
                                            <div className="flex items-center gap-2 text-xs">
                                                <span className={`font-medium ${
                                                    quality.pixel_quality_distribution.pct_below_050 > 40 ? 'text-red-600' :
                                                    quality.pixel_quality_distribution.pct_below_050 > 20 ? 'text-yellow-600' : 'text-green-600'
                                                }`}>
                                                    {quality.pixel_quality_distribution.pct_below_050}% of pixels below 50% quality
                                                </span>
                                                {quality.pixel_quality_distribution.pct_below_030 > 0 && (
                                                    <span className="text-red-600 font-medium">
                                                        ({quality.pixel_quality_distribution.pct_below_030}% below 30%)
                                                    </span>
                                                )}
                                            </div>
                                        )}
                                        <p className="text-xs text-gray-400 mt-2">
                                            {t('analysisResults.pixelQualityNote', 'Quality reflects cloud-free observation density. Low-quality pixels require stronger evidence for deforestation detection (adaptive z-score thresholds).')}
                                        </p>
                                    </div>
                                </div>
                            )}

                            {quality.coverage_warning && (
                                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                                    <p className="text-xs text-red-900 dark:text-red-100">
                                        <strong>{t('analysisResults.warning')}:</strong> {quality.coverage_warning}
                                    </p>
                                </div>
                            )}

                            {/* Configuration Details */}
                            <div>
                                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                    {t('analysisResults.analysisParameters')}
                                </h3>
                                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2 text-sm">
                                    <ConfigRow label={t('analysisResults.paramDataSource')} value={results.sar_enabled ? "Sentinel-2 L2A + Sentinel-1 SAR (Copernicus)" : "Sentinel-2 L2A (Copernicus)"} />
                                    <ConfigRow label={t('analysisResults.paramResolution')} value="10 m/px" />
                                    {results.actual_baseline_range && <ConfigRow label={t('analysisResults.paramBaselinePeriod')} value={results.actual_baseline_range} />}
                                    {results.actual_monitor_range && <ConfigRow label={t('analysisResults.paramMonitorPeriod')} value={results.actual_monitor_range} />}
                                    {results.monitoring_window_type && <ConfigRow label={t('analysisResults.paramWindowType')} value={results.monitoring_window_type} />}
                                    {results.scenes_acquired && <ConfigRow label={t('analysisResults.paramOpticalScenes')} value={`BL: ${results.scenes_acquired.baseline} | MON: ${results.scenes_acquired.monitor} | Total: ${results.scenes_acquired.total}`} />}
                                    {results.sar_enabled && results.scenes_acquired?.sar_total > 0 && (
                                        <ConfigRow label={t('analysisResults.paramSarScenes')} value={`BL: ${results.scenes_acquired.sar_baseline} | MON: ${results.scenes_acquired.sar_monitor} | Total: ${results.scenes_acquired.sar_total}`} />
                                    )}
                                    <ConfigRow label={t('analysisResults.paramMethod')} value={
                                        results.detection_method === 'hybrid_optical_sar_ensemble'
                                            ? "Dual-index ensemble (NDVI + NBR) + SAR (VH/VV) + z-score"
                                            : results.detection_method
                                            ? "Dual-index ensemble (NDVI + NBR) + z-score"
                                            : "NDVI change detection"
                                    } />
                                    {results.z_score_level && <ConfigRow label={t('analysisResults.paramSignificanceLevel')} value={`z > ${results.z_score_level} (${results.significance_level ?? '95% CI, dual-index'})`} />}
                                    {results.joint_false_positive && <ConfigRow label={t('analysisResults.paramFalsePositiveProb')} value={results.joint_false_positive} />}
                                    <ConfigRow label={t('analysisResults.paramNdviThreshold')} value={`${results.threshold_used ?? -0.15}`} />
                                    {results.nbr_threshold_used && <ConfigRow label={t('analysisResults.paramNbrThreshold')} value={`${results.nbr_threshold_used}`} />}
                                    <ConfigRow label={t('analysisResults.paramCloudCoverMax')} value={`${results.cloud_cover_max ?? 80}%`} />
                                    <ConfigRow label={t('analysisResults.paramMinObsPerPixel')} value={t('analysisResults.paramMinObsValue')} />
                                    {results.detection_method && <ConfigRow label={t('analysisResults.paramMinPatch')} value="5 px (500 m2)" />}
                                    <ConfigRow label={t('analysisResults.paramProjection')} value="EPSG:32717 (UTM 17S)" />
                                </div>
                            </div>
                        </>
                    )}

                    {/* ═══ TAB: IMAGERY ═══ */}
                    {activeTab === 'imagery' && (
                        <>
                            {Object.keys(imageBlobUrls).length > 0 ? (
                                <>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {imageBlobUrls.baseline_ndvi && (
                                            <ImageCard
                                                src={imageBlobUrls.baseline_ndvi}
                                                alt={`NDVI Baseline ${job.baseline_year}`}
                                                label={`NDVI Baseline (${job.baseline_year})`}
                                            />
                                        )}
                                        {imageBlobUrls.monitoring_ndvi && (
                                            <ImageCard
                                                src={imageBlobUrls.monitoring_ndvi}
                                                alt={`NDVI Monitoring ${job.monitoring_year}`}
                                                label={`NDVI Monitor (${job.monitoring_year})`}
                                            />
                                        )}
                                        {imageBlobUrls.change_detection && (
                                            <ImageCard
                                                src={imageBlobUrls.change_detection}
                                                alt="Change Detection"
                                                label={t('analysisResults.changeMap')}
                                            />
                                        )}
                                        {imageBlobUrls.deforestation_mask && (
                                            <ImageCard
                                                src={imageBlobUrls.deforestation_mask}
                                                alt="Deforestation Mask"
                                                label={t('analysisResults.deforestedAreas')}
                                            />
                                        )}
                                    </div>

                                    {/* Observation Density — full width */}
                                    {imageBlobUrls.quality_obs_density && (
                                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                {t('analysisResults.observationDensity')}
                                            </p>
                                            <img
                                                src={imageBlobUrls.quality_obs_density}
                                                alt="Observation Density"
                                                className="w-full h-auto rounded border border-gray-200 dark:border-gray-700"
                                            />
                                            <p className="text-xs text-gray-500 mt-2">
                                                {t('analysisResults.observationDensityDescription')}
                                            </p>
                                        </div>
                                    )}

                                    {/* Pixel Quality Heatmap */}
                                    {imageBlobUrls.pixel_quality_map && (
                                        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
                                            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                {t('analysisResults.pixelQualityMapTitle', 'Pixel Quality — Cloud Impact Map')}
                                            </p>
                                            <img
                                                src={imageBlobUrls.pixel_quality_map}
                                                alt="Pixel Quality Map"
                                                className="w-full h-auto rounded border border-gray-200 dark:border-gray-700"
                                            />
                                            <p className="text-xs text-gray-500 mt-2">
                                                {t('analysisResults.pixelQualityMapDescription', 'Shows per-pixel data quality based on cloud-free observation density. Green pixels have high confidence; red/yellow pixels are cloud-degraded and require stronger evidence for deforestation detection. The NDVI and change detection images above are modulated by this quality map — washed-out areas indicate lower reliability.')}
                                            </p>
                                        </div>
                                    )}

                                    {/* SAR Imagery */}
                                    {(imageBlobUrls.sar_vh_backscatter || imageBlobUrls.sar_change || imageBlobUrls.hybrid_coverage) && (
                                        <>
                                            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mt-4 mb-2 flex items-center gap-2">
                                                <Layers className="w-4 h-4" />
                                                Sentinel-1 SAR (Radar)
                                            </h3>
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                {imageBlobUrls.sar_vh_backscatter && (
                                                    <ImageCard
                                                        src={imageBlobUrls.sar_vh_backscatter}
                                                        alt="SAR VH Backscatter"
                                                        label={t('analysisResults.sarVhBackscatter')}
                                                    />
                                                )}
                                                {imageBlobUrls.sar_change && (
                                                    <ImageCard
                                                        src={imageBlobUrls.sar_change}
                                                        alt="SAR Change Detection"
                                                        label={t('analysisResults.sarChangeDetection')}
                                                    />
                                                )}
                                            </div>
                                            {imageBlobUrls.hybrid_coverage && (
                                                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3 mt-4">
                                                    <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">
                                                        {t('analysisResults.hybridCoverageLabel')}
                                                    </p>
                                                    <img
                                                        src={imageBlobUrls.hybrid_coverage}
                                                        alt="Hybrid Coverage"
                                                        className="w-full h-auto rounded border border-gray-200 dark:border-gray-700"
                                                    />
                                                    <p className="text-xs text-gray-500 mt-2">
                                                        {t('analysisResults.hybridCoverageDescription')}
                                                    </p>
                                                </div>
                                            )}
                                        </>
                                    )}

                                    <p className="text-xs text-gray-500">
                                        {results.sar_enabled
                                            ? t('analysisResults.imageryDescriptionSar')
                                            : t('analysisResults.imageryDescriptionOptical')
                                        }
                                    </p>
                                </>
                            ) : imageryLoading ? (
                                <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                                    <Loader2 className="w-8 h-8 animate-spin text-primary-600 mb-3" />
                                    <p className="text-sm">{t('analysisResults.imageryLoading', 'Loading satellite imagery…')}</p>
                                </div>
                            ) : (
                                <p className="text-sm text-gray-500">{t('analysisResults.noImageryAvailable')}</p>
                            )}
                        </>
                    )}

                    {/* Gallery Tab — Least Cloudy True-Colour per Year */}
                    {activeTab === 'gallery' && (() => {
                        // Derive gallery years from imageBlobUrls keys (gallery_YYYY)
                        const galleryEntries = Object.keys(imageBlobUrls)
                            .filter((k) => k.startsWith('gallery_'))
                            .sort();
                        const galleryYears = galleryEntries.map((k) => k.replace('gallery_', ''));

                        return galleryEntries.length > 0 ? (
                            <>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                    Sentinel-2 true-colour composites — least cloudy scene per year ({galleryYears[0]} – {galleryYears[galleryYears.length - 1]})
                                </p>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {galleryEntries.map((key, i) => (
                                        <ImageCard
                                            key={key}
                                            src={imageBlobUrls[key]}
                                            alt={`Sentinel-2 ${galleryYears[i]}`}
                                            label={`Sentinel-2 — ${galleryYears[i]}`}
                                        />
                                    ))}
                                </div>
                                <p className="text-xs text-gray-500 mt-4">
                                    Each image shows the clearest available Sentinel-2 scene (lowest cloud cover) for the polygon area during that year.
                                </p>
                            </>
                        ) : imageryLoading ? (
                            <div className="flex flex-col items-center justify-center py-16 text-gray-500">
                                <Loader2 className="w-8 h-8 animate-spin text-primary-600 mb-3" />
                                <p className="text-sm">{t('analysisResults.galleryLoading', 'Loading Sentinel-2 composites…')}</p>
                            </div>
                        ) : (
                            <p className="text-sm text-gray-500">
                                No gallery images available. Run a new analysis to generate yearly composites.
                            </p>
                        );
                    })()}

                    {/* ═══ TAB: SCENE REPORT ═══ */}
                    {activeTab === 'scenes' && (() => {
                        const sceneReport = results.scene_report;
                        if (!sceneReport) {
                            return (
                                <p className="text-sm text-gray-500">
                                    {t('analysisResults.sceneReport.notAvailable', 'Scene report not available. Run a new analysis to generate scene-level SCL data.')}
                                </p>
                            );
                        }

                        const summary = sceneReport.summary || {};

                        // SCL class color coding
                        const sclColors: Record<number, string> = {
                            0: 'bg-gray-800 text-white',
                            1: 'bg-pink-600 text-white',
                            2: 'bg-gray-600 text-white',
                            3: 'bg-gray-500 text-white',
                            4: 'bg-green-500 text-white',
                            5: 'bg-yellow-600 text-white',
                            6: 'bg-blue-500 text-white',
                            7: 'bg-gray-400 text-white',
                            8: 'bg-orange-400 text-white',
                            9: 'bg-red-500 text-white',
                            10: 'bg-teal-400 text-white',
                            11: 'bg-cyan-300 text-gray-800',
                        };

                        return (
                            <>
                                {/* Summary Cards */}
                                <div>
                                    <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                                        <FileText className="w-4 h-4" />
                                        {t('analysisResults.sceneReport.title', 'Scene Acceptance Report')}
                                    </h3>
                                    <p className="text-xs text-gray-500 mb-4">
                                        {t('analysisResults.sceneReport.description', 'Detailed report of each Sentinel-2 scene evaluated during analysis. Shows which scenes were accepted or rejected based on SCL (Scene Classification Layer) cloud/shadow masking over the polygon area.')}
                                    </p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center border border-green-200 dark:border-green-800">
                                        <p className="text-xs text-gray-500">{t('analysisResults.sceneReport.acceptedBaseline', 'Accepted (Baseline)')}</p>
                                        <p className="text-2xl font-bold text-green-600">{summary.accepted_baseline ?? 0}</p>
                                        <p className="text-xs text-gray-400">/ {summary.total_scenes_baseline ?? 0}</p>
                                    </div>
                                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center border border-red-200 dark:border-red-800">
                                        <p className="text-xs text-gray-500">{t('analysisResults.sceneReport.rejectedBaseline', 'Rejected (Baseline)')}</p>
                                        <p className="text-2xl font-bold text-red-600">{summary.rejected_baseline ?? 0}</p>
                                        <p className="text-xs text-gray-400">/ {summary.total_scenes_baseline ?? 0}</p>
                                    </div>
                                    <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-3 text-center border border-green-200 dark:border-green-800">
                                        <p className="text-xs text-gray-500">{t('analysisResults.sceneReport.acceptedMonitoring', 'Accepted (Monitoring)')}</p>
                                        <p className="text-2xl font-bold text-green-600">{summary.accepted_monitoring ?? 0}</p>
                                        <p className="text-xs text-gray-400">/ {summary.total_scenes_monitoring ?? 0}</p>
                                    </div>
                                    <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-3 text-center border border-red-200 dark:border-red-800">
                                        <p className="text-xs text-gray-500">{t('analysisResults.sceneReport.rejectedMonitoring', 'Rejected (Monitoring)')}</p>
                                        <p className="text-2xl font-bold text-red-600">{summary.rejected_monitoring ?? 0}</p>
                                        <p className="text-xs text-gray-400">/ {summary.total_scenes_monitoring ?? 0}</p>
                                    </div>
                                </div>

                                {/* SCL Classes Legend */}
                                <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                                    <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-3">
                                        {t('analysisResults.sceneReport.sclLegend', 'SCL Class Legend')}
                                    </h4>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-2">
                                        {(sceneReport.scl_classes_accepted || []).map((cls: any) => (
                                            <div key={cls.id} className="flex items-center gap-2">
                                                <span className={`inline-block w-4 h-4 rounded ${sclColors[cls.id]}`} />
                                                <span className="text-xs text-green-700 dark:text-green-400 font-medium">
                                                    {cls.id}: {cls.label}
                                                </span>
                                            </div>
                                        ))}
                                        {(sceneReport.scl_classes_rejected || []).map((cls: any) => (
                                            <div key={cls.id} className="flex items-center gap-2">
                                                <span className={`inline-block w-4 h-4 rounded ${sclColors[cls.id]}`} />
                                                <span className="text-xs text-red-600 dark:text-red-400">
                                                    {cls.id}: {cls.label}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                    <p className="text-xs text-gray-400 mt-2">
                                        {t('analysisResults.sceneReport.sclNote', 'Green = accepted pixel classes | Red = rejected (masked as NaN). Polygon cloud threshold:')} {sceneReport.polygon_cloud_threshold_pct ?? 70}%
                                    </p>
                                </div>

                                {/* Scene Table — Baseline */}
                                {(sceneReport.baseline || []).length > 0 && (
                                    <SceneTable
                                        title={`Baseline (${job.baseline_year})`}
                                        scenes={sceneReport.baseline}
                                        sclColors={sclColors}
                                        imageBlobUrls={imageBlobUrls}
                                    />
                                )}

                                {/* Scene Table — Monitoring */}
                                {(sceneReport.monitoring || []).length > 0 && (
                                    <SceneTable
                                        title={`Monitoring (${job.monitoring_year})`}
                                        scenes={sceneReport.monitoring}
                                        sclColors={sclColors}
                                        imageBlobUrls={imageBlobUrls}
                                    />
                                )}

                                <div className="bg-gray-50 dark:bg-gray-800/50 border border-line dark:border-gray-700 rounded-lg p-3">
                                    <p className="text-xs text-gray-700 dark:text-gray-300">
                                        <strong>{t('analysisResults.interpretation')}:</strong>{' '}
                                        {t('analysisResults.sceneReport.interpretation', 'Scenes are rejected when >70% of pixels over the polygon are cloud/shadow-masked. At least 3 scenes per period are always kept (the clearest ones). The SCL distribution shows the pixel-level classification breakdown for each scene before masking.')}
                                    </p>
                                </div>
                            </>
                        );
                    })()}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                    <button onClick={onClose} className="btn-secondary">
                        {t('analysisResults.close')}
                    </button>
                </div>
            </div>
        </div>
    );
}

/* ─── Reusable sub-components ──────────────────────────────────────── */

function MetricCard({ label, value, subtitle, highlight }: {
    label: string;
    value: string | undefined;
    subtitle?: string;
    highlight?: 'red' | 'green';
}) {
    return (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            <p className={`text-xl font-bold ${
                highlight === 'red' ? 'text-red-600' :
                highlight === 'green' ? 'text-green-600' :
                'text-gray-900 dark:text-white'
            }`}>
                {value || 'N/A'}
            </p>
            {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
    );
}

function CoverageCard({ label, pct, scenes, meanObs, highlight }: {
    label: string;
    pct?: number;
    scenes?: number;
    meanObs?: number;
    highlight?: boolean;
}) {
    const { t } = useTranslation();
    const pctVal = pct ?? 0;
    return (
        <div className={`rounded-lg p-4 ${
            highlight
                ? 'bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800'
                : 'bg-gray-50 dark:bg-gray-800'
        }`}>
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            <p className="text-2xl font-bold text-gray-900 dark:text-white">{pctVal.toFixed(1)}%</p>
            <div className="mt-2 h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div
                    className={`h-full rounded-full ${
                        pctVal >= 60 ? 'bg-green-500' : pctVal >= 30 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${Math.min(100, pctVal)}%` }}
                />
            </div>
            {scenes != null && (
                <p className="text-xs text-gray-500 mt-2">{scenes} {t('analysisResults.scenes')}, {meanObs?.toFixed(1)} obs/px</p>
            )}
        </div>
    );
}

function ImageCard({ src, alt, label }: { src: string; alt: string; label: string }) {
    return (
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-3">
            <p className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">{label}</p>
            <img
                src={src}
                alt={alt}
                className="w-full h-auto rounded border border-gray-200 dark:border-gray-700"
            />
        </div>
    );
}

function ConfigRow({ label, value }: { label: string; value: string }) {
    return (
        <div className="flex justify-between">
            <span className="text-gray-600 dark:text-gray-400">{label}:</span>
            <span className="font-medium text-gray-900 dark:text-white">{value}</span>
        </div>
    );
}

function SceneTable({ title, scenes, sclColors, imageBlobUrls }: {
    title: string;
    scenes: any[];
    sclColors: Record<number, string>;
    imageBlobUrls?: Record<string, string>;
}) {
    const [expandedIdx, setExpandedIdx] = useState<number | null>(null);

    return (
        <div>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2 flex items-center gap-2">
                <Layers className="w-4 h-4" />
                {title} — {scenes.length} scenes
            </h3>
            <div className="overflow-x-auto">
                <table className="w-full text-xs">
                    <thead>
                        <tr className="border-b border-gray-200 dark:border-gray-700 min-w-[640px]">
                            <th className="text-left py-2 px-3 text-gray-500 font-medium">Date</th>
                            <th className="text-center py-2 px-3 text-gray-500 font-medium">Status</th>
                            <th className="text-right py-2 px-3 text-gray-500 font-medium">Clear %</th>
                            <th className="text-right py-2 px-3 text-gray-500 font-medium">Cloud %</th>
                            <th className="text-right py-2 px-3 text-gray-500 font-medium">Shadow %</th>
                            <th className="text-center py-2 px-3 text-gray-500 font-medium">SCL</th>
                        </tr>
                    </thead>
                    <tbody>
                        {scenes.map((scene: any, idx: number) => {
                            const isAccepted = scene.status === 'accepted';
                            const isExpanded = expandedIdx === idx;
                            const sclDist = scene.scl_distribution || {};
                            const hasSclData = Object.keys(sclDist).length > 0;

                            return (
                                <tr key={idx}>
                                    <td colSpan={6} className="p-0">
                                        <div
                                            className={`flex items-center border-b cursor-pointer transition-colors min-w-[640px] ${
                                                isAccepted
                                                    ? 'border-gray-100 dark:border-gray-800 hover:bg-green-50 dark:hover:bg-green-900/10'
                                                    : 'border-gray-100 dark:border-gray-800 bg-red-50/50 dark:bg-red-900/10 hover:bg-red-50 dark:hover:bg-red-900/20'
                                            }`}
                                            onClick={() => hasSclData && setExpandedIdx(isExpanded ? null : idx)}
                                        >
                                            <span className="py-2 px-3 w-[20%] font-mono text-gray-700 dark:text-gray-300">
                                                {scene.date}
                                            </span>
                                            <span className="py-2 px-3 w-[15%] text-center">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                                    isAccepted
                                                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                                                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                                                }`}>
                                                    {isAccepted ? 'Accepted' : 'Rejected'}
                                                </span>
                                            </span>
                                            <span className="py-2 px-3 w-[15%] text-right font-mono text-green-600">
                                                {scene.clear_pct}%
                                            </span>
                                            <span className="py-2 px-3 w-[15%] text-right font-mono text-orange-600">
                                                {scene.scl_cloud_pct ?? scene.cloud_pct ?? '—'}%
                                            </span>
                                            <span className="py-2 px-3 w-[15%] text-right font-mono text-gray-500">
                                                {scene.scl_shadow_pct ?? '—'}%
                                            </span>
                                            <span className="py-2 px-3 w-[20%] text-center">
                                                {hasSclData ? (
                                                    <span className="text-gray-400 text-xs">
                                                        {isExpanded ? '▲ collapse' : '▼ detail'}
                                                    </span>
                                                ) : '—'}
                                            </span>
                                        </div>
                                        {/* Expanded SCL Distribution */}
                                        {isExpanded && hasSclData && (
                                            <div className="px-3 py-3 bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
                                                <p className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2">
                                                    SCL Pixel Distribution — {scene.date}
                                                </p>
                                                {/* Visual bar chart */}
                                                <div className="flex h-6 rounded-lg overflow-hidden mb-2">
                                                    {Object.entries(sclDist)
                                                        .sort(([a], [b]) => Number(a) - Number(b))
                                                        .map(([clsId, data]: [string, any]) => {
                                                            if (data.pct < 0.5) return null;
                                                            return (
                                                                <div
                                                                    key={clsId}
                                                                    className={`${sclColors[Number(clsId)]} flex items-center justify-center text-[10px] font-medium`}
                                                                    style={{ width: `${Math.max(data.pct, 2)}%` }}
                                                                    title={`${data.label}: ${data.pct}% (${data.pixels} px)`}
                                                                >
                                                                    {data.pct >= 5 ? `${data.pct}%` : ''}
                                                                </div>
                                                            );
                                                        })}
                                                </div>
                                                {/* Class details */}
                                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-1">
                                                    {Object.entries(sclDist)
                                                        .sort(([, a]: [string, any], [, b]: [string, any]) => b.pct - a.pct)
                                                        .map(([clsId, data]: [string, any]) => (
                                                            <div key={clsId} className="flex items-center gap-1.5">
                                                                <span className={`inline-block w-3 h-3 rounded-sm flex-shrink-0 ${sclColors[Number(clsId)]}`} />
                                                                <span className="text-[10px] text-gray-600 dark:text-gray-400 truncate">
                                                                    {data.label}: <strong>{data.pct}%</strong> ({data.pixels.toLocaleString()} px)
                                                                </span>
                                                            </div>
                                                        ))}
                                                </div>
                                                {/* Scene Thumbnail */}
                                                {scene.thumbnail_key && imageBlobUrls?.[scene.thumbnail_key] && (
                                                    <div className="mt-3">
                                                        <img
                                                            src={imageBlobUrls[scene.thumbnail_key]}
                                                            alt={`Scene ${scene.date}`}
                                                            className={`w-full max-w-md h-auto rounded border-2 ${
                                                                isAccepted
                                                                    ? 'border-green-300 dark:border-green-700'
                                                                    : 'border-red-300 dark:border-red-700'
                                                            }`}
                                                        />
                                                    </div>
                                                )}
                                                {scene.rejection_reason && (
                                                    <p className="text-[10px] text-red-600 dark:text-red-400 mt-2 font-medium">
                                                        Rejection reason: {scene.rejection_reason}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
